<script setup lang="ts">
import { ref } from 'vue';
import type { Geofence } from '@gpx-spa/geo-domain';
import { Form } from '@primevue/forms';
import ShipmentFormStopCard from '@/components/shipment/form/stops/ShipmentFormStopCard.vue';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import type { ShipmentFormEditContext } from '@/components/shipment/form/types/shipment-form-page-context';

type PlannedTimeField = 'plannedDeparture' | 'plannedArrival';

defineProps<{
  stops: ShipmentFormStopRow[];
  geofences: Geofence[];
  geofencesLoading: boolean;
  placesDisabled: boolean;
  stopsResolver: unknown;
  stopFieldErrorsFor: (index: number) => Record<string, string>;
  suppressPlannedTimeManualMark: boolean;
  editContext?: ShipmentFormEditContext;
}>();

const emit = defineEmits<{
  plannedTimeUserEdit: [
    stopKey: string,
    field: PlannedTimeField,
    value: Date | null,
  ];
}>();

type StopCardExpose = {
  focusFirstField?: () => boolean;
};

const firstStopCardRef = ref<StopCardExpose | null>(null);

function setStopCardRef(el: unknown, idx: number) {
  if (idx === 0) {
    firstStopCardRef.value = (el as StopCardExpose | null) ?? null;
  }
}

function focusFirstField(): boolean {
  return firstStopCardRef.value?.focusFirstField?.() ?? false;
}

defineExpose({
  focusFirstField,
});
</script>

<template>
  <Form
    :resolver="stopsResolver"
    :validate-on-value-update="false"
    class="block"
    @submit.prevent
  >
    <div class="space-y-4">
      <ShipmentFormStopCard
        v-for="(stop, idx) in stops"
        :key="stop.key"
        :ref="(el) => setStopCardRef(el, idx)"
        :stop="stop"
        :index="idx"
        :total-stops="stops.length"
        :geofences="geofences"
        :geofences-loading="geofencesLoading"
        :places-disabled="placesDisabled"
        :field-errors="stopFieldErrorsFor(idx)"
        :suppress-planned-time-manual-mark="suppressPlannedTimeManualMark"
        :disabled-fields="editContext?.getStopDisabledFields?.(idx, stops.length)"
        :lock-reason="editContext?.getStopLockReason?.(idx, stops.length)"
        :lock-badge-label="editContext?.getStopLockBadgeLabel?.(idx, stops.length)"
        :hide-actions="editContext?.hideWaypointControls"
        @planned-time-user-edit="
          (field, value) => emit('plannedTimeUserEdit', stop.key, field, value)
        "
      />
    </div>
  </Form>
</template>
